import React from 'react';
import { TenantConfig } from '@askporter/client-grieg-lyric';
import {
  MAINTENANCE_ENDED_MESSAGE,
  MAINTENANCE_MESSAGE,
  WindowMessage,
  WindowMessageWithPayload,
} from '../../../types';

export const useWindowMessage = (): TenantConfig => {
  const [tenantConfig, setData] = React.useState<TenantConfig>(null);

  React.useEffect(() => {
    const handleMessage = (event: WindowMessageWithPayload) => {
      if (event.data?.type === 'config') setData(event.data.payload);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });

  return tenantConfig;
};

export const useWindowSize = (): { width: number; height: number } => {
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  React.useLayoutEffect(() => {
    const updateSize = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
};

export const useReloadChat = (): number => {
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    const handleMessage = (event: WindowMessage) => {
      if (event.data === 'reload') {
        setReloadKey((prevKey) => prevKey + 1);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });

  return reloadKey;
};

/**
 * Host-side half of the maintenance contract (NS-2464).
 *
 * This used to latch: it set the state on `maintenance` and nothing ever set it
 * back, so a host page stayed in the maintenance state for the life of the page
 * even after the window ended. Fixing the iframe alone would not have been
 * enough — both ends latched, so the state now clears on `maintenance-ended`.
 *
 * See the contract in `../../../types`. `maintenance` may carry a payload; it is
 * ignored here because this hook only reports whether a window is open.
 */
export const useMaintenanceMode = (): boolean => {
  const [isMaintenanceMode, setIsMaintenanceMode] = React.useState(false);

  React.useEffect(() => {
    const handleMessage = (event: WindowMessageWithPayload) => {
      if (event.data?.type === MAINTENANCE_MESSAGE) setIsMaintenanceMode(true);
      if (event.data?.type === MAINTENANCE_ENDED_MESSAGE) setIsMaintenanceMode(false);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });

  return isMaintenanceMode;
};

export const useWindowMessageClose = (closeFn: () => void): void => {
  React.useEffect(() => {
    const handleMessage = (event: WindowMessage) => {
      if (event.data === 'close') {
        closeFn();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  });
};
